import { useEffect, useState } from "react";
import { CalendarDays, Inbox, Loader2, MessageCircle, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { EnquiryCalendar } from "./EnquiryCalendar";

const STATUSES = ["new", "contacted", "responded", "completed", "closed"];

const STATUS_STYLES = {
  new: "bg-[#D4AF37]/15 text-[#8a6d1f]",
  contacted: "bg-[#E8D5CE] text-[#5A504B]",
  responded: "bg-green-100 text-green-800",
  completed: "bg-emerald-100 text-emerald-800",
  closed: "bg-gray-200 text-gray-600",
};

const EnquiryCard = ({ enquiry, onSaved, onDeleted, reviewUrl }) => {
  const [status, setStatus] = useState(enquiry.status || "new");
  const [notes, setNotes] = useState(enquiry.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/enquiries/${enquiry.id}`, { status, notes });
      if (data?.review_whatsapp_sent) toast.success("Enquiry completed — review request sent on WhatsApp");
      else toast.success("Enquiry updated");
      onSaved();
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const reviewMessage = () => {
    const link = reviewUrl ? ` ${reviewUrl}` : "";
    return `Dear ${enquiry.name}, thank you for visiting Royal Skin Clinic today. If you loved your experience, would you share it on Google?${link} — your words help others find us. With gratitude, Dr. Javeria's team.`;
  };

  const remove = async () => {
    if (!window.confirm(`Delete enquiry from ${enquiry.name}?`)) return;
    try {
      await api.delete(`/admin/enquiries/${enquiry.id}`);
      toast.success("Enquiry deleted");
      onDeleted();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="glass rounded-3xl p-6" data-testid={`enquiry-card-${enquiry.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-[#1A1615]">{enquiry.name}</p>
          <p className="text-xs text-[#91837C]">
            {new Date(enquiry.created_at).toLocaleString()} · {enquiry.email} · {enquiry.phone}
          </p>
        </div>
        <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] ${STATUS_STYLES[status] || STATUS_STYLES.new}`}>
          {status}
        </span>
      </div>
      <div className="mt-4 grid gap-3 text-sm text-[#5A504B] sm:grid-cols-2">
        <p><span className="font-medium text-[#1A1615]">Treatment:</span> {enquiry.treatment || "Not specified"}</p>
        <p><span className="font-medium text-[#1A1615]">Preferred date:</span> {enquiry.preferred_date || "Flexible"}{enquiry.preferred_time ? ` · ${enquiry.preferred_time}` : ""}</p>
      </div>
      {enquiry.message && (
        <p className="mt-3 rounded-xl bg-[#F5EBE6]/60 p-4 text-sm font-light italic text-[#5A504B]">“{enquiry.message}”</p>
      )}
      <div className="mt-4 grid gap-3 sm:grid-cols-[180px_1fr_auto_auto]">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid={`enquiry-status-${enquiry.id}`}
          aria-label="Enquiry status"
          className="input-lux py-2.5 text-xs"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal notes..."
          data-testid={`enquiry-notes-${enquiry.id}`}
          className="input-lux py-2.5 text-xs"
        />
        <button
          onClick={save}
          disabled={saving}
          data-testid={`enquiry-save-${enquiry.id}`}
          className="btn-gold px-4 py-2.5 text-xs disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Save className="h-3.5 w-3.5" aria-hidden="true" />}
          Save
        </button>
        <button
          onClick={remove}
          data-testid={`enquiry-delete-${enquiry.id}`}
          aria-label={`Delete enquiry from ${enquiry.name}`}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-red-200 text-red-500 transition-colors hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {status === "completed" && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-emerald-50/70 p-3">
          <p className="text-[11px] font-light text-[#5A504B]">
            {enquiry.review_sent_at
              ? `Review request sent ${new Date(enquiry.review_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} —`
              : "Appointment complete —"}
            {reviewUrl ? " ask for the Google review while the glow is fresh." : " add your Google review link in Site Settings → Contact to auto-attach it."}
          </p>
          <a
            href={`https://wa.me/${(enquiry.phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(reviewMessage())}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`enquiry-review-request-${enquiry.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-white transition-transform hover:-translate-y-0.5"
          >
            <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" /> Send Review Request
          </a>
        </div>
      )}
    </div>
  );
};

export const EnquiriesTab = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("inbox");
  const [capacity, setCapacity] = useState(4);
  const { content } = useContent();

  const load = () => {
    setLoading(true);
    api
      .get("/admin/enquiries")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load enquiries"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);
  useEffect(() => {
    api.get("/content").then((r) => {
      const c = parseInt(r.data?.settings?.slot_capacity, 10);
      if (c > 0) setCapacity(c);
    }).catch(() => {});
  }, []);

  const filtered = filter === "all" ? items : items.filter((e) => e.status === filter);

  return (
    <div data-testid="admin-enquiries-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Enquiries Inbox</h2>
        <div className="flex rounded-full border border-[#1A1A1A]/10 bg-white/60 p-1">
          {[
            { id: "inbox", label: "Inbox", icon: Inbox },
            { id: "calendar", label: "Calendar", icon: CalendarDays },
          ].map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              data-testid={`enquiries-view-${v.id}`}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-all duration-300 ${
                view === v.id ? "bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] text-[#1A1615] shadow" : "text-[#5A504B] hover:text-[#B38B2D]"
              }`}
            >
              <v.icon className="h-3.5 w-3.5" aria-hidden="true" /> {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === "calendar" ? (
        <div className="mt-6">
          <EnquiryCalendar enquiries={items} capacity={capacity} onRefresh={load} />
        </div>
      ) : (
        <>
          <div className="mt-5 flex flex-wrap gap-2">
            {["all", ...STATUSES].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`enquiry-filter-${f}`}
                className={`rounded-full px-4 py-2 text-xs font-medium capitalize transition-[background-color,color] duration-300 ${
                  filter === f ? "bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] text-[#1A1615]" : "bg-white/60 text-[#5A504B] hover:text-[#B38B2D]"
                }`}
              >
                {f} {f === "all" ? `(${items.length})` : `(${items.filter((e) => e.status === f).length})`}
              </button>
            ))}
          </div>
          <div className="mt-6 space-y-4">
            {loading && <p className="text-sm text-[#91837C]">Loading enquiries...</p>}
            {!loading && filtered.length === 0 && (
              <p className="glass rounded-2xl p-6 text-sm font-light text-[#91837C]">No enquiries in this view yet.</p>
            )}
            {filtered.map((e) => (
              <EnquiryCard key={e.id} enquiry={e} onSaved={load} onDeleted={load} reviewUrl={content?.settings?.google_review_url} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
